import {
    DSAR_DETAILS_FIELD_IDS,
    DSAR_REQUEST_SOURCES,
    DSAR_REQUEST_SOURCE_LABELS,
} from '/b2b/dsarForms/constants';
import {
    getLabelForJurisdictionOption,
    jurisdictionOptions,
} from '/b2b/common/helpers/Jurisdiction';
import { DSAR_STATUS } from '/b2b/dsarRequests/constants';
import { NODE_TYPES } from '../../constants';

// --- Operators ---

const EQUALS = { value: 'equals', label: 'dsarWorkflows.decision.operators.equals', multi: false };
const NOT_EQUALS = {
    value: 'notEquals',
    label: 'dsarWorkflows.decision.operators.notEquals',
    multi: false,
};
const CONTAINS = {
    value: 'contains',
    label: 'dsarWorkflows.decision.operators.contains',
    multi: false,
};
const ANY_OF = { value: 'anyOf', label: 'dsarWorkflows.decision.operators.anyOf', multi: true };
const NOT_ANY_OF = {
    value: 'notAnyOf',
    label: 'dsarWorkflows.decision.operators.notAnyOf',
    multi: true,
};
const IS_NULL = {
    value: 'isNull',
    label: 'dsarWorkflows.decision.operators.isNull',
    multi: false,
    staticOptions: [
        { value: 'true', label: 'common.true' },
        { value: 'false', label: 'common.false' },
    ],
};

// Operator sets per value type
const STRING_OPERATORS = [EQUALS, NOT_EQUALS, CONTAINS, IS_NULL];
const SELECT_OPERATORS = [EQUALS, NOT_EQUALS, ANY_OF, NOT_ANY_OF, IS_NULL];
const DATE_OPERATORS = [
    EQUALS,
    NOT_EQUALS,
    {
        value: 'greaterThan',
        label: 'dsarWorkflows.decision.operators.after',
        multi: false,
    },
    {
        value: 'greaterThanOrEqual',
        label: 'dsarWorkflows.decision.operators.onOrAfter',
        multi: false,
    },
    {
        value: 'lessThan',
        label: 'dsarWorkflows.decision.operators.before',
        multi: false,
    },
    {
        value: 'lessThanOrEqual',
        label: 'dsarWorkflows.decision.operators.onOrBefore',
        multi: false,
    },
    IS_NULL,
];

// Full list exported for custom field fallback
export const DECISION_OPERATORS = [
    EQUALS,
    NOT_EQUALS,
    CONTAINS,
    ANY_OF,
    NOT_ANY_OF,
    {
        value: 'greaterThan',
        label: 'dsarWorkflows.decision.operators.greaterThan',
        multi: false,
    },
    {
        value: 'greaterThanOrEqual',
        label: 'dsarWorkflows.decision.operators.greaterThanOrEqual',
        multi: false,
    },
    {
        value: 'lessThan',
        label: 'dsarWorkflows.decision.operators.lessThan',
        multi: false,
    },
    {
        value: 'lessThanOrEqual',
        label: 'dsarWorkflows.decision.operators.lessThanOrEqual',
        multi: false,
    },
    IS_NULL,
];

// Lookup: operator value → operator definition (for checking multi flag)
export const OPERATORS_BY_VALUE = Object.fromEntries(DECISION_OPERATORS.map(o => [o.value, o]));

// A switch routes by value membership, so it only offers the positive-match
// operators — "is" (equals) and "is any of" (anyOf). It excludes notEquals,
// notAnyOf, and isNull.
export const SWITCH_OPERATORS = [EQUALS.value, ANY_OF.value];

// --- Value types ---
// Determines what the value input renders and which operators are available.
export const VALUE_TYPES = {
    TEXT: 'TEXT', // free-text TextField, all string operators
    STATIC_SELECT: 'STATIC_SELECT', // Select from a static options array
    ASYNC_SELECT: 'ASYNC_SELECT', // Select populated by an API fetch on dialog mount
    DATE: 'DATE', // DatePicker input, date comparison operators
};

// --- Status options (static, from constants) ---
const STATUS_OPTIONS = Object.entries(DSAR_STATUS).map(([key, i18nLabel]) => ({
    value: key,
    label: i18nLabel,
}));

// --- Jurisdiction options (static, from env config) ---
// The stored conditionValue is the uppercased jurisdiction code (e.g. US-CA), even
// though the env config holds it lowercased (us-ca).
const JURISDICTION_OPTIONS = jurisdictionOptions.map(opt => ({
    value: opt.value.toUpperCase(),
    label: getLabelForJurisdictionOption(opt),
    isCountry: opt.value === opt.country,
}));

// --- Origin / request source options (static subset matching RequestList filter) ---
const ORIGIN_SOURCE_KEYS = [
    DSAR_REQUEST_SOURCES.WEB,
    DSAR_REQUEST_SOURCES.EMAIL_INTAKE,
    DSAR_REQUEST_SOURCES.CSV,
    DSAR_REQUEST_SOURCES.AGENT_WEB,
];

const ORIGIN_OPTIONS = ORIGIN_SOURCE_KEYS.map(key => ({
    value: key,
    label: DSAR_REQUEST_SOURCE_LABELS[key],
}));

// --- Field definitions ---
// Each field: { path, label (i18n key), operators, valueType, staticOptions?, asyncKey? }
// asyncKey is used by the dialog to know which fetched dataset to use for options.
// Group keys for the unified Autocomplete groupBy
export const FIELD_GROUPS = {
    PARENT_SEARCH: 'dsarWorkflows.decision.fieldGroup.parentSearch',
    REQUEST_INFO: 'dsarWorkflows.decision.fieldGroup.requestInfo',
    FORM_FIELDS: 'dsarWorkflows.decision.fieldGroup.formFields',
};

const GROUPS = {
    REQUEST_INFO: FIELD_GROUPS.REQUEST_INFO,
};

// Paths of the standard fields whose stored branch values aren't their display
// text — request-type/status (i18n keys) and jurisdiction codes — so edge chips
// need them translated/resolved for display.
export const COUNTRY_FIELD_PATH = 'input.requestMetadata.jurisdiction';
// The request-type field is keyed by the camelCase requestType on the input,
// not the hyphenated request-type field id used elsewhere.
export const REQUEST_TYPE_FIELD_PATH = 'input.requestType';
export const STATUS_FIELD_PATH = `input.${DSAR_DETAILS_FIELD_IDS.STATUS}`;

// --- Field definitions ---
// Each field: { path, label (i18n key), operators, valueType, staticOptions?, asyncKey? }
// asyncKey is used by the dialog to know which fetched dataset to use for options.
export const DECISION_FIELDS = [
    {
        path: REQUEST_TYPE_FIELD_PATH,
        label: 'common.requestType',
        group: GROUPS.REQUEST_INFO,
        operators: SELECT_OPERATORS,
        valueType: VALUE_TYPES.ASYNC_SELECT,
        asyncKey: 'requestTypes',
    },
    {
        path: STATUS_FIELD_PATH,
        label: 'common.requestStatus',
        group: GROUPS.REQUEST_INFO,
        operators: SELECT_OPERATORS,
        valueType: VALUE_TYPES.STATIC_SELECT,
        staticOptions: STATUS_OPTIONS,
    },
    {
        path: COUNTRY_FIELD_PATH,
        label: 'common.jurisdiction',
        group: GROUPS.REQUEST_INFO,
        operators: SELECT_OPERATORS,
        valueType: VALUE_TYPES.STATIC_SELECT,
        staticOptions: JURISDICTION_OPTIONS,
    },
    {
        path: `input.${DSAR_DETAILS_FIELD_IDS.REQUEST_SOURCE}`,
        label: 'common.origin',
        group: GROUPS.REQUEST_INFO,
        operators: SELECT_OPERATORS,
        valueType: VALUE_TYPES.STATIC_SELECT,
        staticOptions: ORIGIN_OPTIONS,
    },
    {
        path: `input.requestDetails.${DSAR_DETAILS_FIELD_IDS.EMAIL}`,
        label: 'common.requestor',
        group: GROUPS.REQUEST_INFO,
        operators: STRING_OPERATORS,
        valueType: VALUE_TYPES.TEXT,
    },
];

// Grouped for the dropdown UI — sorted alphabetically by translated label
export const DECISION_FIELD_GROUPS = Object.entries(
    DECISION_FIELDS.reduce((acc, field) => {
        if (!acc[field.group]) {
            acc[field.group] = [];
        }
        acc[field.group].push(field);
        return acc;
    }, {})
).map(([groupLabel, fields]) => ({ label: groupLabel, fields }));

// Lookup map: path → field definition
export const DECISION_FIELDS_BY_PATH = Object.fromEntries(DECISION_FIELDS.map(f => [f.path, f]));

// --- Custom form field support ---

// Map DSAR form field input_type to decision operator set and value behavior
export const FORM_FIELD_TYPE_CONFIG = {
    text: { operators: STRING_OPERATORS, valueType: VALUE_TYPES.TEXT },
    textarea: { operators: STRING_OPERATORS, valueType: VALUE_TYPES.TEXT },
    email: { operators: STRING_OPERATORS, valueType: VALUE_TYPES.TEXT },
    select: { operators: SELECT_OPERATORS, valueType: VALUE_TYPES.STATIC_SELECT },
    multiselect: { operators: SELECT_OPERATORS, valueType: VALUE_TYPES.STATIC_SELECT },
    radio: { operators: SELECT_OPERATORS, valueType: VALUE_TYPES.STATIC_SELECT },
    checkbox: {
        operators: [EQUALS, NOT_EQUALS, IS_NULL],
        valueType: VALUE_TYPES.STATIC_SELECT,
        staticOptions: [
            { value: 'on', label: 'dsarWorkflows.decision.values.checked' },
            { value: '', label: 'dsarWorkflows.decision.values.unchecked' },
        ],
    },
    datepicker: { operators: DATE_OPERATORS, valueType: VALUE_TYPES.DATE },
    hidden: { operators: STRING_OPERATORS, valueType: VALUE_TYPES.TEXT },
};

// Field types that should not appear as decision field options
export const EXCLUDED_FIELD_TYPES = new Set(['readonly', 'file', 'phone']);

// The parent-output field a previous-outcome decision branches on. A decision is
// "previous outcome" when it holds a single rule on this field — its path is
// `parents.<parentId>.resultsFound` once wired, or the bare marker before then.
export const PREVIOUS_OUTCOME_FIELD = 'resultsFound';

// --- Parent node output fields ---

// Output fields available per eligible node type
const RESULTS_FOUND_FIELD = {
    field: 'resultsFound',
    // menuLabel is not used — menu item shows the connection name instead
    operators: [EQUALS, NOT_EQUALS],
    valueType: VALUE_TYPES.STATIC_SELECT,
    staticOptions: [
        { value: 'true', label: 'dsarWorkflows.decision.parentOutput.resultsFound' },
        { value: 'false', label: 'dsarWorkflows.decision.parentOutput.resultsNotFound' },
    ],
};

export const PARENT_OUTPUT_FIELDS = {
    [NODE_TYPES.DSAR_AUTOMATED_ACTION]: [RESULTS_FOUND_FIELD],
    [NODE_TYPES.DSAR_MANUAL_TASK]: [RESULTS_FOUND_FIELD],
};

// --- Decision branches ---

// What the decision branches on. Drives which fields the FIELD picker offers:
// FORM_DATA → DSAR request + form fields, PREVIOUS_OUTCOME → connected parent steps.
export const BRANCH_BASED_ON = {
    FORM_DATA: 'formData',
    PREVIOUS_OUTCOME: 'previousOutcome',
};

// Sentinel conditionValue for the implicit catch-all branch edge.
export const DECISION_DEFAULT_CONDITION = '__edge__';

// Fixed branch conditionValues when branching on a previous step's outcome.
// FAILED terminates the workflow (like an approval's rejected branch).
export const DECISION_OUTCOME_CONDITIONS = {
    SUCCEEDED: 'true',
    FAILED: 'false',
};

// A branch pairs an operator with a value; the field is shared across all branches.
// An implicit "anything else" catch-all branch always trails the explicit ones.
export const NEW_BRANCH = { operator: 'equals', value: '' };

// Radio options for the "Branch based on" selector.
export const BASED_ON_OPTIONS = [
    {
        value: BRANCH_BASED_ON.FORM_DATA,
        title: 'dsarWorkflows.addDecision.basedOn.formData.title',
        description: 'dsarWorkflows.addDecision.basedOn.formData.description',
    },
    {
        value: BRANCH_BASED_ON.PREVIOUS_OUTCOME,
        title: 'dsarWorkflows.addDecision.basedOn.previousOutcome.title',
        description: 'dsarWorkflows.addDecision.basedOn.previousOutcome.description',
    },
];
